import { useEffect, useRef, useState } from "react";
import { GroupInboxConfigService } from "../../../../../../shared/api/services/groupInbox/groupInboxConfigService";
import {
  GroupInboxRoute,
  GROUP_INBOX_ROUTES,
} from "../../../../../../shared/api/services/groupInbox/groupInboxConfigTypes";
import { mapGroupInboxConfigsToGiConfigurations } from "../utils/mapGroupInboxConfigsToGiConfigurations";

/** Default Group Inbox config source until the user switches to API. */
const DEFAULT_GROUP_INBOX_ROUTE = GROUP_INBOX_ROUTES.RPA;

/**
 * Represents the state of GI configurations fetched from the backend.
 */
interface GiConfigurationState {
  /** Array of GI configurations, or null if not yet fetched */
  data: GiConfiguration[] | null;
  /** Whether the configurations are currently being fetched */
  loading: boolean;
  /** Error message if fetching failed, or null if no error */
  error: string | null;
}

/**
 * Represents a single GI (Group Inbox) configuration document.
 */
export interface GiConfiguration {
  /** Unique identifier for the configuration */
  id: string;
  /** Display name for the view tab */
  view_tab?: string;
  /** Version identifier for the configuration */
  version?: string;
  /** Active status of the configuration ("active" or "inactive") */
  status?: string;
  /** Organization ID this configuration belongs to */
  org_id?: string;
  /** ISO timestamp when the configuration was created */
  created_at?: string;
  /** Email of the user who created the configuration */
  created_by?: string;
  /** Email of the user who last updated the configuration */
  last_updated_by?: string;
  /** ISO timestamp when the configuration was last updated */
  last_updated?: string;
  /** RPA configuration payload array */
  config?: any[];
  /** Number of orders associated with this configuration */
  num_orders?: number;
}

/**
 * True when a fetch was cancelled because the facility changed or the hook unmounted.
 *
 * @param error - Caught value from the Group Inbox config request
 * @returns Whether the error is an abort
 */
const isAbortError = (error: unknown): boolean =>
  (error instanceof DOMException || error instanceof Error) &&
  error.name === "AbortError";

/**
 * True when this fetch should not write to React state.
 *
 * A fetch is stale if it was aborted, or if a newer request already replaced
 * it on `abortControllerRef`.
 *
 * @param signal - Abort signal passed to this fetch
 * @param inFlightController - Controller currently stored on the hook ref
 * @returns Whether state updates from this fetch must be ignored
 */
const isStaleGiConfigurationFetch = (
  signal: AbortSignal,
  inFlightController: AbortController | null,
): boolean => signal.aborted || inFlightController?.signal !== signal;

/**
 * Custom hook that manages GI configurations for a selected healthcare facility.
 *
 * This hook handles:
 * - Tracking which healthcare facility is currently selected
 * - Tracking the Group Inbox config route (`rpa` or `api`)
 * - Fetching the latest stored Group Inbox configs via the backend API
 * - Managing loading and error states during data fetching
 * - Aborting an in-flight request on unmount, facility change, or route change
 *
 * @returns The hook's state and setters
 * @returns selectedHealthCareFacility - The currently selected healthcare facility ID
 * @returns setSelectedHealthCareFacility - Setter to change the selected facility
 * @returns selectedGroupInboxRoute - Config source passed as `route` on the fetch
 * @returns setSelectedGroupInboxRoute - Setter to switch between `rpa` and `api`
 * @returns giConfigurationState - Object containing data, loading, and error states
 * @returns refreshGiConfigurations - Re-fetches the list after a successful mutation
 *
 * @example
 * const {
 *   selectedHealthCareFacility,
 *   setSelectedHealthCareFacility,
 *   giConfigurationState,
 * } = useGiConfiguration();
 *
 * setSelectedHealthCareFacility("facility-123");
 */
export const useGiConfiguration = () => {
  const [selectedHealthCareFacility, setSelectedHealthCareFacility] =
    useState<string>("");
  const [selectedGroupInboxRoute, setSelectedGroupInboxRoute] =
    useState<GroupInboxRoute>(DEFAULT_GROUP_INBOX_ROUTE);
  const [giConfigurationState, setGiConfigurationState] =
    useState<GiConfigurationState>({
      data: null,
      loading: false,
      error: null,
    });
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Fetches GI configurations for the currently selected healthcare facility.
   *
   * Sends `selectedGroupInboxRoute` as the backend `route` (`rpa` or `api`).
   * Aborted requests are ignored so a newer facility or route is not overwritten.
   *
   * @param signal - Abort signal tied to the current facility and route
   */
  const getGiConfigurationForSelectedHealthCareFacility = async (
    signal: AbortSignal,
  ) => {
    if (!selectedHealthCareFacility) {
      setGiConfigurationState((prev) => ({
        ...prev,
        data: null,
        loading: false,
        error: null,
      }));
      return;
    }

    try {
      setGiConfigurationState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));
      const response = await GroupInboxConfigService.getConfigurations(
        {
          org_id: selectedHealthCareFacility,
          route: selectedGroupInboxRoute,
        },
        signal,
      );
      if (isStaleGiConfigurationFetch(signal, abortControllerRef.current)) {
        return;
      }
      setGiConfigurationState({
        data: mapGroupInboxConfigsToGiConfigurations(
          response.configs ?? [],
          selectedHealthCareFacility,
        ),
        loading: false,
        error: null,
      });
    } catch (error) {
      if (
        isAbortError(error) ||
        isStaleGiConfigurationFetch(signal, abortControllerRef.current)
      ) {
        return;
      }
      setGiConfigurationState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : String(error),
        loading: false,
      }));
    }
  };

  /**
   * Aborts the current in-flight fetch, if any, and starts a new one for
   * the facility and route from this render.
   */
  const startGiConfigurationFetch = () => {
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    void getGiConfigurationForSelectedHealthCareFacility(
      abortController.signal,
    );
  };

  /**
   * Re-fetches GI configurations for the currently selected facility and
   * route after an external mutation (e.g. saving a new or edited
   * configuration) so the list reflects the latest data.
   *
   * Aborts any in-flight request first so stale results cannot overwrite
   * the refreshed data.
   */
  const refreshGiConfigurations = () => {
    startGiConfigurationFetch();
  };

  /**
   * Fetches GI configurations whenever the selected facility or route changes.
   *
   * Cleanup aborts whatever request is currently in `abortControllerRef`,
   * including a refresh started after this effect ran, so a superseded
   * fetch cannot overwrite a newer facility or route.
   */
  useEffect(() => {
    startGiConfigurationFetch();

    return () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, [selectedHealthCareFacility, selectedGroupInboxRoute]);

  return {
    selectedHealthCareFacility,
    setSelectedHealthCareFacility,
    selectedGroupInboxRoute,
    setSelectedGroupInboxRoute,
    giConfigurationState,
    refreshGiConfigurations,
  };
};
